import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_ADMIN_CODE } from '@kome/shared';
import { login, AdminApiError } from '@/lib/api';
import { useAdminStore } from '@/lib/store';
import Field from '@/components/Field';
import Input from '@/components/Input';
import Button from '@/components/Button';

/**
 * 관리자 로그인 (코드 인증) — 인증 게이트.
 *
 * 흐름: 관리자 코드(개발용 426846) 입력 → POST /admin/login →
 *       성공 시 adminToken 저장(useAdminStore.setSession) → /dashboard(또는 원래 위치)로 이동.
 * 실패 시 상태에 맞는 한국어 안내 문구를 노출한다.
 *
 * 보안: 관리자 코드는 개발 편의용이며 운영 전 반드시 강화(관리자 계정+2FA)한다. (SPEC §9)
 */
export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAdminStore((s) => s.setSession);
  const isAuthed = useAdminStore((s) => s.isAuthed);

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // 가드가 넘겨준 원래 목적지(없으면 대시보드)
  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/dashboard';

  // 이미 로그인되어 있으면 곧장 목적지로 (로그인 화면 재노출 방지)
  if (isAuthed) {
    return <Navigate to={redirectTo} replace />;
  }

  /** API/네트워크 오류를 사용자용 한국어 문구로 변환 */
  const messageFromError = (err: unknown): string => {
    if (err instanceof AdminApiError) {
      if (err.status === 0) return '서버에 연결할 수 없습니다. 네트워크를 확인해 주세요.';
      if (err.status === 401 || err.status === 403) return '관리자 코드가 올바르지 않습니다.';
      return err.message || '로그인에 실패했습니다.';
    }
    return err instanceof Error ? err.message : '로그인에 실패했습니다.';
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed || loading) return;

    setError(null);
    setLoading(true);
    try {
      const res = await login(trimmed);
      setSession(res.adminToken, res.expiresAt);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(messageFromError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={onSubmit} noValidate>
        <div className="login-card__logo" aria-hidden="true">
          코
        </div>
        <h1 className="login-card__title">코메 관리자</h1>
        <p className="login-card__sub">광고 없는 회사 전용 AI 메신저</p>

        <Field
          htmlFor="admin-code"
          error={error ?? undefined}
          hint={`개발용 기본 코드: ${DEFAULT_ADMIN_CODE}`}
        >
          <Input
            id="admin-code"
            name="admin-code"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            autoFocus
            aria-label="관리자 코드"
            placeholder="관리자 코드"
            value={code}
            invalid={!!error}
            disabled={loading}
            onChange={(e) => {
              // 숫자만 허용 (관리자 코드는 숫자 형식)
              setCode(e.target.value.replace(/\D/g, ''));
              if (error) setError(null);
            }}
          />
        </Field>

        <Button
          type="submit"
          variant="primary"
          loading={loading}
          disabled={!code.trim()}
          style={{ width: '100%' }}
        >
          로그인
        </Button>

        <p
          className="muted"
          style={{
            marginTop: 'var(--space-xl)',
            marginBottom: 0,
            fontSize: 'var(--font-size-xs)',
            lineHeight: 1.5,
            textAlign: 'left',
          }}
        >
          ⚠️ 관리자 코드는 개발 편의용입니다. 운영 배포 전에는 관리자 계정과 2단계 인증으로
          반드시 보안을 강화해 주세요.
        </p>
      </form>
    </div>
  );
}
